/**
 * Template Builder - Preview Step
 * 
 * Comprehensive preview of the template before submission
 */

import React from 'react';
import { View, StyleSheet, ScrollView, Image, Platform } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption, SubHeading } from '@/components/ui/Text';
import { Ionicons } from '@expo/vector-icons';
import type { TemplateDay, TravelStyle } from '@/types/tripTemplates';

type TemplateBuilderPreviewStepProps = {
  draft: any;
};

export const TemplateBuilderPreviewStep: React.FC<TemplateBuilderPreviewStepProps> = ({
  draft,
}) => {
  const theme = useTheme();

  const itinerary = draft.itinerary || [];
  const totalActivities = itinerary.reduce(
    (sum: number, day: TemplateDay) => sum + (day.activities?.length || 0),
    0
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header Preview */}
      <Card style={styles.headerCard}>
        {draft.coverImage && (
          <Image
            source={{ uri: draft.coverImage }}
            style={styles.coverImage}
            resizeMode="cover"
          />
        )}
        <View style={styles.headerContent}>
          <SubHeading style={styles.previewTitle}>{draft.title || 'Untitled Template'}</SubHeading>
          <Body color={theme.colors.textSecondary} style={styles.previewDestination}>
            {draft.destination || 'No destination'}
          </Body>
          <View style={styles.previewMeta}>
            <View style={styles.metaItem}>
              <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>
                {draft.duration || 0} days
              </Caption>
            </View>
            <View style={styles.metaItem}>
              <Ionicons name="flag-outline" size={16} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>
                {draft.difficulty || 'N/A'}
              </Caption>
            </View>
            <View style={styles.metaItem}>
              <Ionicons name="wallet-outline" size={16} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>
                {draft.estimatedBudget?.currency || 'USD'}{' '}
                {draft.estimatedBudget?.min?.toLocaleString() || 0} -{' '}
                {draft.estimatedBudget?.max?.toLocaleString() || 0}
              </Caption>
            </View>
          </View>
        </View>
      </Card>

      {/* Description */}
      {draft.shortDescription && (
        <Card style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Description</ThemedText>
          <Body color={theme.colors.text}>{draft.shortDescription}</Body>
          {draft.longDescription && (
            <Body color={theme.colors.text} style={styles.longDescription}>
              {draft.longDescription}
            </Body>
          )}
        </Card>
      )}

      {/* Travel Styles & Tags */}
      {(draft.travelStyle?.length > 0 || draft.tags?.length > 0) && (
        <Card style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Travel Style & Tags</ThemedText>
          <View style={styles.chipContainer}>
            {draft.travelStyle?.map((style: TravelStyle) => (
              <View key={style} style={[styles.chip, { backgroundColor: theme.colors.primary + '15' }]}>
                <Caption style={[styles.chipText, { color: theme.colors.primary }]}>
                  {style}
                </Caption>
              </View>
            ))}
            {draft.tags?.map((tag: string) => (
              <View key={tag} style={[styles.chip, { backgroundColor: theme.colors.borderMuted }]}>
                <Caption style={styles.chipText}>{tag}</Caption>
              </View>
            ))}
          </View>
        </Card>
      )}

      {/* Best Months */}
      {draft.bestMonths?.length > 0 && (
        <Card style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Best Months to Visit</ThemedText>
          <View style={styles.chipContainer}>
            {draft.bestMonths.map((month: string) => (
              <View key={month} style={[styles.chip, { backgroundColor: theme.colors.borderMuted }]}>
                <Caption style={styles.chipText}>{month}</Caption>
              </View>
            ))}
          </View>
        </Card>
      )}

      {/* Budget Breakdown */}
      {draft.estimatedBudget?.breakdown && (
        <Card style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Budget Breakdown</ThemedText>
          <View style={styles.budgetBreakdown}>
            {Object.entries(draft.estimatedBudget.breakdown).map(([category, amount]: [string, any]) => {
              if (!amount || amount === 0) return null;
              const total = Object.values(draft.estimatedBudget.breakdown).reduce(
                (sum: number, val: any) => sum + (val || 0),
                0
              );
              const percentage = total > 0 ? Math.round((amount / total) * 100) : 0;

              return (
                <View key={category} style={styles.budgetItem}>
                  <View style={styles.budgetItemHeader}>
                    <Body style={styles.budgetCategory}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </Body>
                    <Body style={styles.budgetAmount}>
                      {draft.estimatedBudget.currency || 'USD'}
                      {amount.toLocaleString()}
                    </Body>
                  </View>
                  <View style={styles.budgetBar}>
                    <View
                      style={[
                        styles.budgetBarFill,
                        {
                          width: `${percentage}%`,
                          backgroundColor: theme.colors.primary,
                        },
                      ]}
                    />
                  </View>
                  <Caption color={theme.colors.textSecondary} style={styles.budgetPercentage}>
                    {percentage}%
                  </Caption>
                </View>
              );
            })}
          </View>
        </Card>
      )}

      {/* Itinerary Preview */}
      <Card style={styles.section}>
        <View style={styles.itineraryHeader}>
          <ThemedText style={styles.sectionTitle}>Itinerary</ThemedText>
          <Caption color={theme.colors.textSecondary}>
            {itinerary.length} days • {totalActivities} activities
          </Caption>
        </View>

        {itinerary.length === 0 ? (
          <View style={styles.emptyItinerary}>
            <Ionicons name="calendar-outline" size={32} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary}>
              No itinerary days added yet
            </Caption>
          </View>
        ) : (
          <View style={styles.itineraryList}>
            {itinerary.slice(0, 5).map((day: TemplateDay, dayIndex: number) => (
              <View key={dayIndex} style={styles.dayPreview}>
                <View style={styles.dayPreviewHeader}>
                  <ThemedText style={styles.dayPreviewTitle}>
                    {day.title || `Day ${day.day}`}
                  </ThemedText>
                  <Caption color={theme.colors.textSecondary}>
                    {day.activities?.length || 0} activities
                  </Caption>
                </View>
                {day.activities && day.activities.length > 0 && (
                  <View style={styles.activitiesPreview}>
                    {day.activities.slice(0, 3).map((activity: any, activityIndex: number) => (
                      <View key={activityIndex} style={styles.activityPreview}>
                        <View style={styles.activityTimeBadge}>
                          <Caption style={styles.activityTimeText}>{activity.time}</Caption>
                        </View>
                        <Body style={styles.activityPreviewTitle}>{activity.title}</Body>
                      </View>
                    ))}
                    {day.activities.length > 3 && (
                      <Caption color={theme.colors.textSecondary} style={styles.moreActivities}>
                        +{day.activities.length - 3} more activities
                      </Caption>
                    )}
                  </View>
                )}
              </View>
            ))}
            {itinerary.length > 5 && (
              <Caption color={theme.colors.textSecondary} style={styles.moreDays}>
                +{itinerary.length - 5} more days
              </Caption>
            )}
          </View>
        )}
      </Card>

      {/* Highlights */}
      {draft.highlights && draft.highlights.length > 0 && (
        <Card style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Highlights</ThemedText>
          <View style={styles.highlightsList}>
            {draft.highlights.map((highlight: string, index: number) => (
              <View key={index} style={styles.highlightItem}>
                <Ionicons name="star" size={16} color={theme.colors.primary} />
                <Body style={styles.highlightText}>{highlight}</Body>
              </View>
            ))}
          </View>
        </Card>
      )}

      {/* Submission Info */}
      <Card style={[styles.section, { backgroundColor: theme.colors.primary + '10' }]}>
        <View style={styles.submissionInfo}>
          <Ionicons name="information-circle-outline" size={24} color={theme.colors.primary} />
          <View style={styles.submissionText}>
            <ThemedText style={styles.submissionTitle}>Ready to Submit</ThemedText>
            <Caption color={theme.colors.textSecondary}>
              Your template will be reviewed before being published. You'll be notified once it's approved.
            </Caption>
          </View>
        </View>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  headerCard: {
    padding: 0,
    overflow: 'hidden',
    marginBottom: 16,
  },
  coverImage: {
    width: '100%',
    height: 200,
  },
  headerContent: {
    padding: 16,
  },
  previewTitle: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  previewDestination: {
    fontSize: 16,
    marginBottom: 12,
  },
  previewMeta: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  section: {
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
  },
  longDescription: {
    marginTop: 8,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  chipText: {
    fontSize: 12,
    fontWeight: '500',
  },
  budgetBreakdown: {
    gap: 12,
  },
  budgetItem: {
    gap: 4,
  },
  budgetItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  budgetCategory: {
    fontSize: 14,
    fontWeight: '500',
  },
  budgetAmount: {
    fontSize: 14,
    fontWeight: '600',
  },
  budgetBar: {
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  budgetBarFill: {
    height: '100%',
    borderRadius: 2,
  },
  budgetPercentage: {
    fontSize: 11,
  },
  itineraryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  emptyItinerary: {
    padding: 32,
    alignItems: 'center',
    gap: 8,
  },
  itineraryList: {
    gap: 16,
  },
  dayPreview: {
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
  },
  dayPreviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  dayPreviewTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  activitiesPreview: {
    gap: 8,
  },
  activityPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  activityTimeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.1)',
    minWidth: 50,
  },
  activityTimeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  activityPreviewTitle: {
    fontSize: 14,
    flex: 1,
  },
  moreActivities: {
    marginLeft: 62,
    fontSize: 12,
  },
  moreDays: {
    textAlign: 'center',
    marginTop: 8,
  },
  highlightsList: {
    gap: 12,
  },
  highlightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  highlightText: {
    flex: 1,
    fontSize: 14,
  },
  submissionInfo: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  submissionText: {
    flex: 1,
    gap: 4,
  },
  submissionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
});
